import type { DocHandle, DocumentId, Repo } from "@automerge/automerge-repo";
import { moveCardToTrash } from "../../widgets/narthex/trash-widget";
import type { CanvasDocument } from "../canvas/canvas-doc";
import { CanvasStore } from "../canvas/canvas-store";
import {
  isRecentlyUnavailable,
  markResolved,
  markUnavailable,
  resolveDocReadyCached,
  watchDocReady,
} from "../p2p/doc-ready";
import { log } from "@freqhole/reliquary/utils";

const TAG = "canvas.watchers";

/**
 * iterates all canvas-card widgets in the narthex, opens each linked canvas
 * document, and copies fresh metadata (title, description, lastModified,
 * color, previewUrl, lastModifiedBy) into the card's per-widget doc. also
 * handles hasUpdates logic (using lastModifiedBy to distinguish own vs remote
 * edits) and the lastVisitedAt one-time migration for pre-schema cards.
 */
export async function syncCanvasMetadataToCards(
  repo: Repo,
  narthexStore: CanvasStore,
  localNodeId: string
): Promise<void> {
  const widgets = narthexStore.allWidgets();

  for (const entry of widgets) {
    if (entry.type !== "canvas-card" || !entry.docId) continue;

    try {
      // read the card's per-widget doc to get the canvasDocId
      const cardHandle = await resolveDocReadyCached<Record<string, unknown>>(
        repo,
        entry.docId as DocumentId
      );
      if (!cardHandle) continue;
      const cardDoc = cardHandle.doc();
      if (!cardDoc?.canvasDocId || typeof cardDoc.canvasDocId !== "string") continue;

      // open the linked canvas document and read its metadata. CanvasStore.open()
      // doesn't have its own negative cache, so gate it with the same
      // isRecentlyUnavailable/markResolved/markUnavailable helpers directly.
      if (isRecentlyUnavailable(cardDoc.canvasDocId)) continue;
      let canvasStore: CanvasStore;
      try {
        canvasStore = await CanvasStore.open(repo, cardDoc.canvasDocId as DocumentId, {
          timeoutMs: 15_000,
        });
        markResolved(cardDoc.canvasDocId);
      } catch (err) {
        markUnavailable(cardDoc.canvasDocId);
        throw err;
      }
      const meta = canvasStore.metadata();

      // purge: auto-remove the card and all linked docs immediately.
      // narthexStore.removeWidget triggers reconciliation → unmountWidget(permanent=true)
      // → beforeRemoveHook which deletes the canvas doc + all widget state docs.
      if (meta.deleted && meta.deleteMode === "purge") {
        log.debug(
          TAG,
          "purge detected for canvas:",
          (cardDoc.canvasDocId as string).slice(0, 16) + "...",
          "— auto-removing card"
        );
        narthexStore.removeWidget(entry.id);
        continue;
      }

      // perform all updates in a single change() to avoid stale-snapshot bugs.
      // reading from the draft (d) instead of a pre-read cardDoc means the
      // hasUpdates decision sees the freshly-written metadata values.
      let changed = false;
      cardHandle.change((d: any) => {
        // sync metadata fields from the canvas doc
        if (meta.title && meta.title !== (d.title ?? "")) {
          d.title = meta.title;
          changed = true;
        }
        if (meta.description !== undefined && meta.description !== (d.description ?? "")) {
          d.description = meta.description;
          changed = true;
        }
        if (meta.lastModified && meta.lastModified !== (d.modifiedAt ?? "")) {
          d.modifiedAt = meta.lastModified;
          changed = true;
        }
        // sync color from canvas doc (0 means "not set" -- skip to keep card's own default)
        if (meta.color && meta.color !== (d.color ?? 0)) {
          d.color = meta.color;
          changed = true;
        }
        // sync previewUrl from canvas doc
        if (meta.previewUrl !== undefined && meta.previewUrl !== (d.previewUrl ?? "")) {
          log.debug(
            TAG,
            `one-shot-syncing previewUrl for card ${entry.id}: ` +
              `${JSON.stringify(d.previewUrl ?? "").slice(0, 60)} -> ${JSON.stringify(meta.previewUrl).slice(0, 60)}`
          );
          d.previewUrl = meta.previewUrl;
          changed = true;
        }
        // sync lastModifiedBy from canvas doc
        if (meta.lastModifiedBy !== undefined && meta.lastModifiedBy !== (d.lastModifiedBy ?? "")) {
          d.lastModifiedBy = meta.lastModifiedBy;
          changed = true;
        }

        // sync tombstone fields from canvas doc — owner may have deleted
        // (or un-deleted) the canvas since we last synced
        if (meta.deleted) {
          if (!d.isDeleted) {
            d.isDeleted = true;
            changed = true;
          }
          const deletedAt = meta.deletedAt || "";
          if (deletedAt !== (d.deletedAt ?? "")) {
            d.deletedAt = deletedAt;
            changed = true;
          }
          const deletedBy = meta.deletedBy || "";
          if (deletedBy !== (d.deletedBy ?? "")) {
            d.deletedBy = deletedBy;
            changed = true;
          }
          const deleteMode = meta.deleteMode || "soft";
          if (deleteMode !== (d.deleteMode ?? "")) {
            d.deleteMode = deleteMode;
            changed = true;
          }
        } else {
          // canvas is not deleted (or was un-deleted) — clear any stale tombstone fields
          if (d.isDeleted) {
            d.isDeleted = false;
            d.deletedAt = "";
            d.deletedBy = "";
            d.deleteMode = "";
            changed = true;
          }
        }

        // seed lastVisitedAt for pre-migration cards (one-time migration).
        // without this, cards created before the schema migration have
        // lastVisitedAt = "" which causes:
        //   - stuck update pills on shared canvases (any lastModified > "")
        //   - missing pills on owned canvases (sync skips empty lastVisitedAt)
        const lastVisited = (d.lastVisitedAt as string) || "";
        if (!lastVisited && meta.lastModified) {
          d.lastVisitedAt = meta.lastModified;
          changed = true;
          // after seeding, this canvas is "caught up" -- no update pill
          if (d.hasUpdates) {
            d.hasUpdates = false;
          }
          return;
        }

        // set or clear hasUpdates based on who last modified the canvas.
        // own edits never trigger the pill — only remote changes do.
        const modifiedByOther = meta.lastModifiedBy && meta.lastModifiedBy !== localNodeId;
        if (
          modifiedByOther &&
          meta.lastModified &&
          lastVisited &&
          meta.lastModified > lastVisited
        ) {
          d.hasUpdates = true;
          d.lastKnownModifiedAt = meta.lastModified;
          changed = true;
        } else if (!modifiedByOther && d.hasUpdates) {
          // own edit was the last change — clear any stale pill
          d.hasUpdates = false;
          changed = true;
        }
      });

      if (changed) {
        log.debug(TAG, "synced metadata to canvas-card:", entry.id);
      }

      // auto-collect soft-deleted cards into the trash widget.
      // moveCardToTrash is a no-op if the card is already in the trash
      // or if no trash widget exists in the narthex.
      if (meta.deleted && meta.deleteMode !== "purge" && !entry.parentId) {
        await moveCardToTrash(repo, narthexStore, entry.id);
      }
    } catch (err) {
      // if a canvas doc isn't reachable, skip silently
      log.warn(TAG, "failed to sync metadata for card:", entry.id, err);
    }
  }
}

/**
 * watches all canvas docs linked from narthex cards for remote changes.
 * when a canvas doc's lastModified changes (via automerge sync) and the
 * change was made by a different node (lastModifiedBy !== localNodeId),
 * marks the card with hasUpdates so the update dot appears. own edits
 * are ignored since the user already sees them.
 *
 * returns an array of unsubscribe functions -- the caller is responsible
 * for invoking them when watchers should be torn down.
 */
export async function watchCanvasDocsForUpdates(
  repo: Repo,
  narthexStore: CanvasStore,
  localNodeId: string
): Promise<Array<() => void>> {
  const unsubs: Array<() => void> = [];
  const widgets = narthexStore.allWidgets();

  for (const entry of widgets) {
    if (entry.type !== "canvas-card" || !entry.docId) continue;
    const cardDocId = entry.docId;

    try {
      const cardHandle = await resolveDocReadyCached<Record<string, unknown>>(
        repo,
        cardDocId as DocumentId
      );
      if (!cardHandle) continue;
      const cardDoc = cardHandle.doc();
      if (!cardDoc?.canvasDocId || typeof cardDoc.canvasDocId !== "string") continue;

      const canvasDocId = cardDoc.canvasDocId as string;

      let lastSeenModified = (cardDoc.modifiedAt as string) || "";

      const onChange = (canvasHandle: DocHandle<CanvasDocument>, detach: () => void) => {
        const canvasDoc = canvasHandle.doc();
        if (!canvasDoc) return;

        // detect tombstone changes from remote peers — sync deletion
        // fields to the card so the narthex can reflect the state promptly
        if (canvasDoc.deleted) {
          // purge: auto-remove the card immediately — the beforeRemoveHook
          // cascade will clean up the canvas doc + all widget state docs
          if (canvasDoc.deleteMode === "purge") {
            log.debug(
              TAG,
              "purge detected via sync:",
              canvasDocId.slice(0, 16) + "...",
              "— auto-removing card"
            );
            narthexStore.removeWidget(entry.id);
            detach();
            return;
          }

          cardHandle.change((draft: any) => {
            if (!draft.isDeleted) {
              draft.isDeleted = true;
              draft.deletedAt = canvasDoc.deletedAt || "";
              draft.deletedBy = canvasDoc.deletedBy || "";
              draft.deleteMode = canvasDoc.deleteMode || "soft";
            }
          });

          // auto-collect into the trash widget if not already there
          if (!entry.parentId) {
            moveCardToTrash(repo, narthexStore, entry.id).catch((err) => {
              log.warn(TAG, "failed to auto-collect card into trash:", err);
            });
          }

          // no need to process update pills for a deleted canvas
          return;
        } else if (!canvasDoc.deleted) {
          // canvas was un-deleted — clear tombstone fields on the card
          cardHandle.change((draft: any) => {
            if (draft.isDeleted) {
              draft.isDeleted = false;
              draft.deletedAt = "";
              draft.deletedBy = "";
              draft.deleteMode = "";
            }
          });
        }

        if (!canvasDoc.lastModified) return;
        if (canvasDoc.lastModified === lastSeenModified) return;
        lastSeenModified = canvasDoc.lastModified;

        // sync the actual metadata (title/description/color/previewUrl)
        // regardless of who made the edit — this used to be skipped
        // entirely for the local peer's OWN edits (bundled into the same
        // early-return as the "don't show a pill for my own edit" check
        // below), which meant setting a canvas's preview image (or
        // renaming/recoloring it) while narthex was already mounted (not
        // freshly (re)navigated-to, which is the only thing that runs the
        // separate one-shot `syncCanvasMetadataToCards()`) never reached
        // the card at all until the next full narthex remount. "don't
        // show the update pill for my own edit" and "don't sync the
        // underlying data for my own edit" are different concerns; only
        // the pill should stay remote-only (see below).
        cardHandle.change((draft: any) => {
          if (canvasDoc.title && canvasDoc.title !== (draft.title ?? "")) draft.title = canvasDoc.title;
          if (canvasDoc.description !== undefined && canvasDoc.description !== (draft.description ?? "")) {
            draft.description = canvasDoc.description;
          }
          if (canvasDoc.color && canvasDoc.color !== (draft.color ?? 0)) draft.color = canvasDoc.color;
          if (canvasDoc.previewUrl !== undefined && canvasDoc.previewUrl !== (draft.previewUrl ?? "")) {
            log.debug(
              TAG,
              `live-syncing previewUrl for card ${entry.id} (canvas ${canvasDocId.slice(0, 12)}): ` +
                `${JSON.stringify(draft.previewUrl ?? "").slice(0, 60)} -> ${JSON.stringify(canvasDoc.previewUrl).slice(0, 60)}`
            );
            draft.previewUrl = canvasDoc.previewUrl;
          }
        });

        // only show the pill for remote changes — own edits are already visible
        if (!canvasDoc.lastModifiedBy || canvasDoc.lastModifiedBy === localNodeId) return;

        // don't mark as updated if user is currently viewing this canvas
        const currentHash = window.location.hash.slice(1);
        if (currentHash === canvasDocId) return;

        cardHandle.change((draft: any) => {
          draft.hasUpdates = true;
          draft.lastKnownModifiedAt = canvasDoc.lastModified;
          draft.modifiedAt = canvasDoc.lastModified;
          draft.lastModifiedBy = canvasDoc.lastModifiedBy;
        });
      };

      // this listener lives for as long as the narthex is mounted, so it
      // uses the unbounded, event-driven watcher rather than a bounded
      // one-shot resolve: a canvas that's unreachable right now should
      // still start getting update pills the moment it becomes reachable
      // later, without waiting for the next narthex visit to notice.
      let detachChangeListener: (() => void) | undefined;
      const readyUnsub = watchDocReady<CanvasDocument>(repo, canvasDocId as DocumentId, (canvasHandle) => {
        const listener = () => onChange(canvasHandle, detachChangeListener!);
        canvasHandle.on("change", listener);
        detachChangeListener = () => canvasHandle.off("change", listener);
      });

      unsubs.push(() => {
        readyUnsub();
        detachChangeListener?.();
      });
    } catch {
      // skip unavailable docs
    }
  }

  return unsubs;
}
